import { createClient } from '@supabase/supabase-js';
import { logger } from './utils';

// Get environment variables - these must be set in .env.local
const getSupabaseUrl = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) {
    logger.error('VITE_SUPABASE_URL is not defined. Please add it to your .env.local file.');
  }
  return url;
};

const getSupabaseAnonKey = () => {
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!key) {
    logger.error('VITE_SUPABASE_ANON_KEY is not defined. Please add it to your .env.local file.');
  }
  return key;
};

// Create a Supabase client with the anon key for client-side use
// This client should ONLY be used for authentication and authorized operations
export const supabase = createClient(
  getSupabaseUrl(),
  getSupabaseAnonKey(),
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
    // Disable direct table access for sensitive tables
    // This won't completely prevent access but adds a layer of protection
    global: {
      headers: {
        'x-client-info': 'adressfinder-client'
      }
    }
  }
);

// Export a function to check if we're in a server environment
// This helps components decide whether to use API endpoints or direct imports
export const isServerEnvironment = () => {
  return typeof window === 'undefined';
};


// Database types based on our schema
export type FunnelStep = 
  | 'personal_data' 
  | 'address_data' 
  | 'known_info' 
  | 'address_details' 
  | 'birthdate' 
  | 'both_info' 
  | 'no_info' 
  | 'email' 
  | 'loading'
  | 'payment' 
  | 'success';

export interface UserSession {
  session_id: string;
  created_at?: string;
  last_active?: string;
  user_agent?: string;
  referrer_url?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  url_query_string?: string;
  first_url?: string;
}

export interface FunnelData {
  id?: number;
  session_id: string;
  step: FunnelStep;
  form_data: Record<string, any>;
  entered_at: string;
  exited_at?: string;
  completed: boolean;
  errors?: Record<string, string>;
  metadata?: Record<string, any>;
}

// Helper function to generate a unique session ID if one doesn't exist
// Make this async so we can wait for the session to be created
// Uses sessionStorage instead of localStorage so each browser tab gets its own session
export const getSessionId = async (): Promise<string> => {
  let sessionId = sessionStorage.getItem('funnel_session_id');
  
  if (!sessionId) {
    // No session ID in sessionStorage, create a new one
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('funnel_session_id', sessionId);
    
    // Create a new user session in the database
    // This is a brand new session, so capture the first URL
    try {
      await createUserSession(sessionId, true);
    } catch (err) {
      logger.error('Error creating new session:', err);
      // Continue anyway, as we at least have the session ID in sessionStorage
    }
  } else {
    // We have a session ID in sessionStorage, but let's make sure it exists in the database
    // Instead of querying first (which might cause 406 errors), just try to create it
    // The insert will fail silently if the session already exists due to the UNIQUE constraint
    // Don't send first_url for existing sessions - we want to preserve the original
    try {
      await createUserSession(sessionId, false);
    } catch (err) {
      logger.error('Error ensuring session exists:', err);
      // Continue anyway, as we at least have the session ID in sessionStorage
    }
  }
  
  return sessionId;
};

// Also create a synchronous version for places that can't use async
// Uses sessionStorage instead of localStorage so each browser tab gets its own session
export const getSessionIdSync = (): string => {
  let sessionId = sessionStorage.getItem('funnel_session_id');
  
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('funnel_session_id', sessionId);
    // Create a new user session in the database (don't wait)
    // This is a brand new session, so capture the first URL
    createUserSession(sessionId, true);
  }
  
  return sessionId;
};

// Initialize a user session in the database via Edge Function
export const createUserSession = async (sessionId: string, isNewSession: boolean = false): Promise<void> => {
  try {
    const userAgent = navigator.userAgent;
    const referrer = document.referrer;
    const urlParams = new URLSearchParams(window.location.search);
    const urlQueryString = window.location.search;
    
    // Only capture and send first_url for brand new sessions
    let firstUrl: string | undefined = undefined;
    if (isNewSession) {
      firstUrl = window.location.pathname + window.location.search;
      logger.log('Captured first URL for new session:', firstUrl);
    }
    
    const supabaseUrl = getSupabaseUrl();
    
    const requestBody: any = {
      session_id: sessionId,
      user_agent: userAgent,
      referrer_url: referrer || undefined,
      utm_source: urlParams.get('utm_source') || undefined,
      utm_medium: urlParams.get('utm_medium') || undefined,
      utm_campaign: urlParams.get('utm_campaign') || undefined,
      url_query_string: urlQueryString || undefined,
    };
    
    // Only include first_url if this is a new session
    if (isNewSession && firstUrl) {
      requestBody.first_url = firstUrl;
    }
    
    const response = await fetch(`${supabaseUrl}/functions/v1/initialize-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getSupabaseAnonKey()}`
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to initialize session');
    }

    const result = await response.json();
    logger.log('Session initialized in database:', result);
  } catch (err) {
    logger.error('Failed to create user session:', err);
  }
};


// Capture and store the initial URL query string
export const captureInitialUrlQueryString = (): void => {
  // Only run in browser environment
  if (typeof window === 'undefined') return;
  
  // Check if we already have a stored query string
  const storedQueryString = localStorage.getItem('initial_url_query_string');
  
  // If we don't have a stored query string and the current URL has query parameters, store them
  if (!storedQueryString && window.location.search) {
    logger.log('Capturing initial URL query string:', window.location.search);
    localStorage.setItem('initial_url_query_string', window.location.search);
  }
};

// Get the stored URL query string
export const getStoredUrlQueryString = (): string => {
  // Only run in browser environment
  if (typeof window === 'undefined') return '';
  
  return localStorage.getItem('initial_url_query_string') || '';
};

// Initialize capture of URL query string
if (typeof window !== 'undefined') {
  captureInitialUrlQueryString();
}
